import React, { useState, useEffect, useRef } from 'react';
import { Calendar, DollarSign, ChevronDown, ChevronUp, UserCircle2, Users2, Baby, Heart, Briefcase, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Itinerary, DayPlan, TravelGroup } from '../../types/itinerary';
import { DayTimeline } from './DayTimeline';
import { BudgetBreakdown } from './BudgetBreakdown';
import { format } from 'date-fns';
import { ItineraryHeaderActions } from './ItineraryHeaderActions';
import { ItineraryToolbar } from './ItineraryToolbar';

import { COLOR_SEQUENCE, MARKER_COLORS } from '../../utils/markerUtils';

// PDS - Persistent data system
import { 
  itineraryService, 
  initializeDataSystem 
} from '../../services/dataPersistence';

interface ItineraryPanelProps {
  itinerary: Partial<Itinerary>;
  onLocationSelect: (locationId: string) => void;
  selectedLocationId?: string;
  onLocationsUpdate?: (locations: any[]) => void;
  streamingActivity?: boolean;
  activeDay?: number;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
  onClose?: () => void;
}

const getTravelGroupIcon = (group: string | TravelGroup) => {
  switch (group) {
    case 'Solo traveler': return <UserCircle2 size={20} className="text-white/90" />;
    case 'Friends': return <Users2 size={20} className="text-white/90" />;
    case 'Family with kids': return <Baby size={20} className="text-white/90" />;
    case 'Couple': return <Heart size={20} className="text-white/90" />;
    case 'Business trip': return <Briefcase size={20} className="text-white/90" />;
    default: return <Users2 size={20} className="text-white/90" />;
  }
};

export function ItineraryPanel({ 
  itinerary, 
  onLocationSelect,
  selectedLocationId,
  onLocationsUpdate,
  streamingActivity = false,
  activeDay,
  isVisible = true,
  onToggleVisibility,
  onClose
}: ItineraryPanelProps) {
  const [expandedDays, setExpandedDays] = useState<Set<number>>(() => {
    // If existing itinerary from DB, expand all
    if (itinerary.id) {
      return new Set(Array.from({ length: itinerary.days?.length || 0 }, (_, i) => i));
    }
    // For new itineraries, start with all collapsed
    return new Set();
  });

  const [showBudget, setShowBudget] = useState(false);
  const [isGenerationComplete, setIsGenerationComplete] = useState(false);
  const [completedDays, setCompletedDays] = useState<Set<number>>(new Set());

  const toggleButtonRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Update expanded days when new days are added
  useEffect(() => {
    if (itinerary.days) {
      setExpandedDays(new Set(Array.from({ length: itinerary.days.length }, (_, i) => i)));
    }
  }, [itinerary.days?.length]);

  // Initialize data system if needed
  useEffect(() => {
    initializeDataSystem();
  }, [
    itinerary.tripDetails?.destination,
    itinerary.tripDetails?.startDate,
    itinerary.tripDetails?.endDate
  ]);

  // Sticky header setup
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const dayHeaders = container.querySelectorAll('.day-header');
      const scrollTop = container.scrollTop;

      dayHeaders.forEach((header, index) => {
        const headerElement = header as HTMLElement;
        const headerTop = headerElement.offsetTop;
        const nextHeader = dayHeaders[index + 1] as HTMLElement;
        const nextHeaderTop = nextHeader?.offsetTop || Infinity;

        if (scrollTop >= headerTop - 1 && scrollTop < nextHeaderTop - 48) {
          headerElement.style.position = 'sticky';
          headerElement.style.top = '0';
          headerElement.style.zIndex = '10';
        } else {
          headerElement.style.position = 'relative';
          headerElement.style.top = 'auto';
          headerElement.style.zIndex = 'auto';
        }
      });
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [itinerary.days]);

  // Handle marker click event
  useEffect(() => {
    const handleMarkerClick = (e: Event) => {
      const customEvent = e as CustomEvent;
      const locationId = customEvent.detail?.locationId;
      
      if (locationId && itinerary && itinerary.days) {
        const dayIndex = itinerary.days.findIndex(day => 
          day.activities.some(activity => activity.location.id === locationId)
        );
        
        if (dayIndex !== -1) {
          // Ensure the day is expanded
          setExpandedDays(prev => new Set([...prev, dayIndex]));
          
          setTimeout(() => {
            const activityElement = document.querySelector(`[data-activity-id="${locationId}"]`);
            const container = scrollContainerRef.current;
            if (activityElement && container) {
              const offset = activityElement.getBoundingClientRect().top - container.getBoundingClientRect().top;
              container.scrollTo({
                top: container.scrollTop + offset - 100, // Account for sticky header
                behavior: 'smooth'
              });
            }
          }, 100);
        }
      }
    };
    
    window.addEventListener('tripper-marker-clicked', handleMarkerClick);
    return () => window.removeEventListener('tripper-marker-clicked', handleMarkerClick);
  }, [itinerary]);

  // Update map when new activities are added
  useEffect(() => {
    if (itinerary.days?.length && onLocationsUpdate) {
      const allLocations = itinerary.days.flatMap(day => 
        day.activities?.map(activity => ({
          ...activity.location,
          dayNumber: day.dayNumber
        })) || []
      );
      if (allLocations.length > 0) {
        onLocationsUpdate(allLocations);
      }
    }
  }, [itinerary.days, onLocationsUpdate]);

  // ADD THIS NEW USEEFFECT:
  useEffect(() => {
    if (itinerary.id) {
      // Existing itinerary from DB
      setIsGenerationComplete(true);
    } else if (streamingActivity === false && (itinerary.days?.length ?? 0) > 0) {
      // Streaming has stopped and we have days
      setIsGenerationComplete(true);
    }
  }, [streamingActivity, itinerary.id, itinerary.days?.length]);

  // Add effect to auto-expand active day during streaming
  useEffect(() => {
    if (streamingActivity && activeDay !== undefined) {
      // If activeDay is 1-indexed, convert to 0-indexed for the array
      const dayIndex = activeDay - 1;
      if (dayIndex >= 0) {
        setExpandedDays(prev => {
          const newSet = new Set(prev);
          newSet.add(dayIndex);
          return newSet;
        });
      }
    }
  }, [activeDay, streamingActivity]);

  // Add effect to expand all when streaming completes
  useEffect(() => {
    const days = itinerary.days;
    if (!streamingActivity && days && days.length > 0 && !itinerary.id) {
      // Check if all days have activities
      const allDaysComplete = days.every(day => day.activities?.length > 0);
      if (allDaysComplete) {
        // Expand all days when generation is complete
        setTimeout(() => {
          setExpandedDays(new Set(Array.from({ length: days.length }, (_, i) => i)));
        }, 500); // Small delay for visual effect
      }
    }
  }, [streamingActivity, itinerary.days, itinerary.id]);

  // Add useEffect to track completion
  useEffect(() => {
    if (itinerary.days && !streamingActivity) {
      // Mark all days with activities as complete when streaming stops
      const completed = new Set<number>();
      itinerary.days.forEach((day, index) => {
        if (day.activities && day.activities.length > 0) {
          completed.add(index);
        }
      });
      setCompletedDays(completed);
      // Expand all days when generation is complete
      if (completed.size === itinerary.days.length) {
        setExpandedDays(new Set(Array.from({ length: itinerary.days.length }, (_, i) => i)));
      }
    }
  }, [itinerary.days, streamingActivity]);

  // Add this helper function inside ItineraryPanel component
  const isItineraryComplete = () => {
    // If itinerary has an ID, it's from database - always show toolbar
    if (itinerary.id) return true;
    
    // Otherwise, check if all days have activities
    if (!itinerary.days || itinerary.days.length === 0) return false;
    
    return itinerary.days.every(day => 
      day.activities && day.activities.length > 0
    );
  };

  const handleToggleClick = () => {
    if (onToggleVisibility) {
      onToggleVisibility();
    }
  };

  const getDayStatus = (day: DayPlan, dayIndex: number) => {
    const isActiveDay = activeDay === dayIndex;
    const isCompleted = completedDays.has(dayIndex);
    
    if (isCompleted) {
      return {
        status: 'complete',
        text: `${day.activities.length} activities`,
        bgColor: 'bg-blue-400',
        textColor: 'text-green-600',
        showCheckmark: true
      };
    }
    
    if (isActiveDay && streamingActivity) {
      return {
        status: 'loading',
        text: 'Building your plan...',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-600',
        showSpinner: true
      };
    }
    
    // Pending days
    return {
      status: 'pending',
      text: 'Pending',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-400',
      opacity: 0.5
    };
  };

  return (
    <>
      {/* Toggle Button (always visible) */}
      <div 
        ref={toggleButtonRef}
        className="absolute -right-8 top-1/2 transform -translate-y-1/2 z-50"
      >
        <button
          onClick={handleToggleClick}
          className="bg-white h-16 w-8 rounded-r-lg shadow-md flex items-center justify-center border border-l-0 border-gray-200 hover:bg-gray-50 transition-colors"
          aria-label={isVisible ? "Hide itinerary" : "Show itinerary"}
        >
          {isVisible ? (
            <ChevronLeft size={20} className="text-gray-600" />
          ) : (
            <ChevronRight size={20} className="text-gray-600" />
          )}
        </button>
      </div>
  
      {/* Main content container */}
      <div 
        className="bg-white h-full flex flex-col overflow-hidden"
        style={{ width: '500px', maxWidth: '100%' }}
      >
        {/* Text-based header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 shadow-sm">
          <h2 className="text-xl font-medium mb-2">
            {itinerary.tripDetails?.destination || (
              <div className="h-6 w-48 bg-white/20 animate-pulse rounded" />
            )}
          </h2>
          
          <div className="flex flex-wrap gap-4 text-sm">
            {itinerary.tripDetails?.startDate && itinerary.tripDetails?.endDate ? (
              <div className="flex items-center gap-1.5">
                <Calendar size={14} className="text-white/80" />
                <span>
                  {format(new Date(itinerary.tripDetails.startDate), 'MMM d')} - {' '}
                  {format(new Date(itinerary.tripDetails.endDate), 'MMM d, yyyy')}
                </span>
              </div>
            ) : (
              <div className="h-5 w-36 bg-white/20 animate-pulse rounded" />
            )}
            
            {itinerary.tripDetails?.travelGroup && (
              <div className="flex items-center gap-1.5">
                {getTravelGroupIcon(itinerary.tripDetails.travelGroup as TravelGroup)}
                <span>{itinerary.tripDetails.travelGroup}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Scrollable content */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative bg-white">
          
          <div>
          {itinerary.days?.map((day, index) => {
            const dayNumber = index + 1;
            const isCurrentlyStreaming = streamingActivity && activeDay === dayNumber;
            
            const hasActivities = day.activities && day.activities.length > 0;

            const isLastDay = dayNumber === (itinerary.days?.length ?? 0);

            const isComplete = hasActivities && (
              itinerary.id || // Always complete for DB itineraries with activities
              !streamingActivity
            );
            
            // For DB itineraries, skip the "pending" low-opacity state
            const isFullOpacity = itinerary.id || isCurrentlyStreaming || isComplete;
            
            const isExpanded = expandedDays.has(index);
            
            return (
              <div key={index} className="relative">
                {/* Day Header */}
                <div 
                  className={`day-header bg-white/95 backdrop-blur-sm border-b transition-all duration-500 ${
                    isFullOpacity ? 'opacity-100' : 'opacity-40'
                  }`}
                  data-day-index={index}
                >
                  <button
                    onClick={() => {
                      if (isComplete || !streamingActivity || itinerary.id) {
                        const newExpanded = new Set(expandedDays);
                        if (isExpanded) {
                          newExpanded.delete(index);
                        } else {
                          newExpanded.add(index);
                        }
                        setExpandedDays(newExpanded);
                      }
                    }}
                    className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                      !isComplete && streamingActivity && !itinerary.id ? 'cursor-not-allowed' : 'hover:bg-gray-50/50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Status indicators */}
                      <div className="flex-shrink-0">
                        {(() => {
                          if (isCurrentlyStreaming && !itinerary.id) {
                            return (
                              <div className="w-5 h-5 relative">
                                <div className="absolute inset-0 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                              </div>
                            );
                          }
                          if (isComplete) {
                            return (
                              <div className="w-5 h-5 rounded-full bg-blue-400 flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            );
                          }
                          if (!itinerary.id) {
                            return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
                          }
                          return null;
                        })()}
                      </div>
                      <div className="text-left">
                        <h3 className="text-sm font-medium text-gray-900">
                          {day.date ? format(new Date(day.date), 'EEEE, MMMM d') : 'Day ' + (index + 1)}
                        </h3>
                          <div className="text-xs text-gray-500 h-4">
                          {(() => {
                            if (isCurrentlyStreaming && !itinerary.id) {
                              return 'Building your plan...';
                            }
                            if (hasActivities) {
                              return `${day.activities.length} activities`;
                            }
                            if (!itinerary.id) {
                              return <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />;
                            }
                            return <span className="text-gray-400">Loading...</span>;
                          })()}
                        </div>
                      </div>
                    </div>

                    <ChevronDown 
                      size={20} 
                      className={`text-gray-400 transform transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>
                
                {/* Day Content remains the same */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                  }`}
                >
                  <DayTimeline 
                    day={day}
                    onLocationSelect={onLocationSelect}
                    selectedLocationId={selectedLocationId}
                    isLoading={isCurrentlyStreaming}
                    previousDayComplete={index === 0 || (itinerary.days[index - 1]?.activities?.length > 0)}
                    streamingActivity={streamingActivity}
                    dayNumber={index + 1}
                    activeDay={activeDay}
                    dayColor={MARKER_COLORS[COLOR_SEQUENCE[index % COLOR_SEQUENCE.length]]}
                  />
                </div>
              </div>
            );
          })}
          </div>
          {/* Budget Section - Show when activities are complete or budget exists */}
          {(itinerary.budgetSummary || (!streamingActivity && itinerary.days?.every(day => day.activities?.length > 0))) && (
            <div className="bg-white">
              <button
                onClick={() => {
                  if (itinerary.budgetSummary?.totalEstimatedBudget) {
                    setShowBudget(!showBudget);
                  }
                }}
                className={`w-full px-6 py-4 flex items-center justify-between transition-all duration-500 border-t ${
                  itinerary.budgetSummary?.totalEstimatedBudget 
                    ? 'opacity-100 hover:bg-gray-50' 
                    : 'opacity-40'
                } ${
                  !itinerary.budgetSummary?.totalEstimatedBudget ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Status indicator */}
                  <div className="flex-shrink-0">
                    {/* Show spinner if budget exists but no total yet (means it's loading) */}
                    {itinerary.budgetSummary && !itinerary.budgetSummary.totalEstimatedBudget && (
                      <div className="w-5 h-5 relative">
                        <div className="absolute inset-0 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                      </div>
                    )}
                    {/* Show checkmark only when we have the actual budget amount */}
                    {itinerary.budgetSummary?.totalEstimatedBudget && (
                      <div className="w-5 h-5 rounded-full bg-blue-400 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    {/* Show empty circle if budget section should appear but hasn't started loading */}
                    {!itinerary.budgetSummary && (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                    )}
                  </div>
                  {/* <DollarSign size={20} className="text-green-600" /> */}
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">Budget Breakdown</h3>
                    <div className="text-sm text-gray-500 h-4">
                      {itinerary.budgetSummary?.totalEstimatedBudget ? (
                        <span>{itinerary.budgetSummary.totalEstimatedBudget}</span>
                      ) : (
                        <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                      )}
                    </div>
                  </div>
                </div>
                
                <ChevronDown 
                  size={20} 
                  className={`text-gray-400 transform transition-transform duration-200 ${
                    showBudget ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {showBudget && itinerary.budgetSummary?.totalEstimatedBudget && (
                <div className="px-6 pb-6">
                  <BudgetBreakdown summary={itinerary.budgetSummary} />
                </div>
              )}
            </div>
          )}
        </div>
        {isGenerationComplete && (
          <ItineraryToolbar itinerary={itinerary} />
        )}
      </div>
    </>
  );
}